import { useRouteMatch } from 'quartz-routing/react-router';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { usePlaylistItemSearch } from './PlaylistItemSearchManager';
import PaginatedUICardGrid from 'academy-common-lib/components/layout/PaginatedUICardGrid';
import ApiError from '../../../components/Errors/ApiError';
import NoResultsError from 'academy-common-lib/components/Errors/NoResultsError';
import Raven from 'raven-js';
import { PLAYLIST_SEARCH_RESULTS_SIZE } from '../playlistItemsConsts';
import { useSearchFeedback } from 'academy-common-lib/features/search/searchQueries';
import { useDisplayedData } from 'academy-common-lib/features/table/tableHooks';
import { usePlaylistItemsSearch } from '../playlistItemsHooks';
import LearningContentCard from 'academy-common-lib/features/learningContentCard/components/LearningContentCard';
import { View } from 'foundations-components/transitional/components/View';
import { ResultsMessage } from 'foundations-components/transitional/components/ResultsMessage';
import { Text } from 'foundations-components/components/Text';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const InitialSearchEmptyState = () => {
  return /*#__PURE__*/_jsx(Flex, {
    flexGrow: 1,
    children: /*#__PURE__*/_jsxs(ResultsMessage, {
      alignItems: "center",
      display: "flex",
      flexDirection: "column",
      flexGrow: 1,
      gap: "400",
      illustration: "empty-state-charts",
      illustrationProps: {
        width: 180
      },
      justifyContent: "center",
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h4",
        typography: "heading-300",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.savedItems.playlistDetails.addContentToPlaylistModal.initialEmptyState.title"
        })
      }), /*#__PURE__*/_jsx(Text, {
        typography: "body-200",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.savedItems.playlistDetails.addContentToPlaylistModal.initialEmptyState.subtitle"
        })
      })]
    })
  });
};
export const PlaylistItemSearchResults = ({
  onSearchPageChange,
  AddRemoveButton
}) => {
  const {
    addPlaylistItem,
    removePlaylistItem,
    newPlaylistItems,
    resetQuery,
    itemsAddedCount,
    itemsRemovedCount
  } = usePlaylistItemSearch();
  const [sendSearchFeedback] = useSearchFeedback();
  const {
    url
  } = useRouteMatch();
  const {
    isMobile,
    isTablet
  } = useMediaQuery();
  const playlistHasChanges = itemsAddedCount > 0 || itemsRemovedCount > 0;
  const {
    results,
    loading,
    error,
    totalCount,
    page,
    setPage,
    searchCriteria,
    correlationId,
    isAnyFilterSelected
  } = usePlaylistItemsSearch();
  const {
    results: displayedSearchResults,
    totalCount: displayedTotalCount = 0
  } = useDisplayedData({
    results,
    totalCount
  }, {
    loading,
    parameters: {
      page,
      searchCriteria
    }
  }) || {};
  if (error) {
    return /*#__PURE__*/_jsx(ApiError, {
      error: error,
      marker404: "CATALOG_COURSES_API_404",
      markerDefault: "CATALOG_COURSES_API_ERROR",
      resetLink: url
    });
  }
  const handleOnAddItemClick = ({
    type: contentContainerType,
    resultMLOptionId
  }) => {
    if (!correlationId) return; // only send feedback once per search and per item click

    sendSearchFeedback({
      variables: {
        correlationId,
        contentContainerType,
        resultMLOptionId
      }
    }).catch(Raven.captureException.bind(Raven));
  };
  const handlePageChange = newPage => {
    setPage(newPage);
    if (onSearchPageChange) onSearchPageChange();
  };
  const hasSearchOrFilters = searchCriteria.search || isAnyFilterSelected;
  if (!hasSearchOrFilters && !playlistHasChanges) {
    return /*#__PURE__*/_jsx(InitialSearchEmptyState, {});
  }
  if (!displayedSearchResults) return null;
  return displayedSearchResults.length ? /*#__PURE__*/_jsx(View, {
    marginBlockStart: isMobile ? '500' : '0',
    paddingInline: isMobile ? '0' : '400',
    children: /*#__PURE__*/_jsx(PaginatedUICardGrid, {
      columnSize: 250,
      isLoadingPageOnDemand: true,
      limit: PLAYLIST_SEARCH_RESULTS_SIZE,
      onPageChange: handlePageChange,
      page: page,
      shouldRenderOneColumn: isTablet,
      totalCount: displayedTotalCount,
      children: displayedSearchResults.map((item, index) => {
        const {
          id,
          type,
          language,
          name
        } = item;
        const {
          visibility
        } = item;
        const playlistItem = {
          itemId: Number(id),
          type: item.type,
          language: language,
          name,
          hasBeenRemoved: false,
          visibility: visibility || 'PUBLIC' // doesn't really matter, as items returned by the search are all visible to the user
        };
        const newPlaylistItem = newPlaylistItems.find(i => i.itemId === playlistItem.itemId && i.type === playlistItem.type);
        const isAdded = newPlaylistItem !== undefined && !(newPlaylistItem && newPlaylistItem.hasBeenRemoved);
        const handleToggle = () => {
          if (isAdded) {
            removePlaylistItem(playlistItem);
          } else {
            handleOnAddItemClick(item);
            addPlaylistItem(playlistItem);
          }
        };
        const addRemoveButton = /*#__PURE__*/_jsx(AddRemoveButton, {
          isAdded: isAdded,
          onClick: handleToggle
        });
        return /*#__PURE__*/_jsx(LearningContentCard, {
          "data-test-id": `${type === 'SHORT_FORM_CONTENT' ? 'short' : type === 'TRACK' ? 'track' : 'lesson'}-${id}`,
          disableLink: true,
          item: item,
          menuAlwaysVisible: true,
          onItemClick: handleToggle,
          overviewType: "playlist-items-search-results",
          position: index + 1,
          renderMenu: () => addRemoveButton,
          selected: isAdded,
          size: isTablet ? 'small' : 'large',
          underlineOnHover: false
        }, `item_${id}`);
      })
    })
  }) : /*#__PURE__*/_jsx(NoResultsError, {
    onReset: resetQuery,
    resetLink: url
  });
};